import React, { useCallback, useMemo, useState } from "react";
import { formatISO } from "date-fns";
import { Task } from "@common/types/task";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_COMPARISON_OPTIONS,
} from "@common/types/reports/report-type";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import {
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { AccountingReportCycleSelect } from "@components/accounting/AccountingReportCycleSelect";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { SpreadsheetGrid } from "@components/spreadsheet/SpreadsheetGrid";
import { ColumnDef } from "@components/spreadsheet/types";
import { BaseCellProps } from "@components/spreadsheet/cells";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { pluralizeWithCount } from "@common/helpers/string";
import { AccountingReportComparisonSelect } from "@components/accounting/AccountingReportComparisonSelect";
import { usePendingReferenceContextMaybe } from "@components/tasks/SpreadsheetRefContext";
import { useCellRangeChatAction } from "@components/tasks/chatDrag/useCellRangeChatAction";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useTaskReportFilters } from "../shared/useTaskReportFilters";
import { useViewConfigSync } from "../shared/useViewConfigSync";
import { ExpandCollapseAllButton } from "../shared/ExpandCollapseAllButton";
import { useExpandCollapseAll } from "../shared/useExpandCollapseAll";
import {
    PLSpreadsheetRow,
    CounterpartyFilter,
    buildPeriodColumns,
    buildCounterpartyFilter,
    getDefaultExpandedIds,
} from "./profitLossActivationUtils";
import { hasSingleValueColumn } from "@components/accounting/singleColumnReport";
import {
    REPORT_LOOK_LABEL_COLUMN,
    toReportLookColumns,
} from "../shared/reportLookColumns";
import { buildAmountCellFn } from "./profitLossTaskColumns";
import { useProfitLossReport } from "./useProfitLossReport";
import { useProfitLossSelection } from "./useProfitLossSelection";
import { ProfitLossTaskCommandBar } from "./ProfitLossTaskCommandBar";
import { PLTaskGLDrilldownDrawer } from "./PLTaskGLDrilldownDrawer";
import styles from "./ProfitLossActivationTaskBody.module.scss";

interface Props {
    selectedTask: Task;
}

interface DrawerState {
    accountCodes: number[];
    startDate: string;
    endDate: string;
    counterpartyFilter?: CounterpartyFilter;
}

export const ProfitLossActivationTaskBody: React.FC<Props> = ({
    selectedTask,
}) => {
    const isBlocked = (selectedTask.numberOfActiveBlockedByTasks ?? 0) > 0;

    const {
        localDateRange,
        setLocalDateRange,
        localCycle,
        setLocalCycle,
        comparisonOptions,
        setComparisonOptions,
    } = useTaskReportFilters(selectedTask);

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_PROFIT_LOSS_TASK_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const [drawerState, setDrawerState] = useState<DrawerState | null>(null);
    const entityId = selectedTask.entityIds?.[0];
    const { profitAndLoss, rows, ledgerId, filters } = useProfitLossReport({
        entityId,
        dateRange: localDateRange,
        cycle: localCycle,
        comparisonOptions,
        includeCounterpartyBreakdown: true,
    });

    const defaultExpandedIds = useMemo(
        () => (rows ? getDefaultExpandedIds(rows) : []),
        [rows],
    );

    const {
        expandedIds,
        setExpandedIds,
        expandedIdsSeed,
        isCollapsed,
        handleExpandAll,
        handleCollapseAll,
    } = useExpandCollapseAll(rows, defaultExpandedIds);

    const handleCellDoubleClick = useCallback((params: DrawerState) => {
        setDrawerState(params);
    }, []);

    const handleDrawerClose = useCallback(() => {
        setDrawerState(null);
        void profitAndLoss.refetch();
    }, [profitAndLoss]);

    const handleAmountDrillDown = useCallback(
        (plRow: PLSpreadsheetRow, period: ReportPeriodWithLabel) =>
            handleCellDoubleClick({
                accountCodes: plRow._accountCodes ?? [],
                startDate: period.startDate,
                endDate: period.endDate,
                counterpartyFilter: buildCounterpartyFilter(plRow),
            }),
        [handleCellDoubleClick],
    );

    const amountCellFn = useMemo(
        () =>
            buildAmountCellFn({
                expandedIds,
                onDrillDown: handleAmountDrillDown,
            }),
        [expandedIds, handleAmountDrillDown],
    );

    const labelColumn = useMemo((): ColumnDef => {
        const start = localDateRange.start;
        const end = localDateRange.end;

        return {
            ...REPORT_LOOK_LABEL_COLUMN,
            cellFn: ({ value, row }: BaseCellProps) => {
                const plRow: PLSpreadsheetRow = row;
                const canDrillDown =
                    (plRow._accountCodes?.length ?? 0) > 0 &&
                    start != null &&
                    end != null;
                return (
                    <span
                        style={{
                            cursor: canDrillDown ? "pointer" : undefined,
                        }}
                        onDoubleClick={() => {
                            if (!canDrillDown) {
                                return;
                            }
                            handleCellDoubleClick({
                                accountCodes: plRow._accountCodes ?? [],
                                startDate: formatISO(start, {
                                    representation: "date",
                                }),
                                endDate: formatISO(end, {
                                    representation: "date",
                                }),
                                counterpartyFilter:
                                    buildCounterpartyFilter(plRow),
                            });
                        }}
                    >
                        {String(value ?? "")}
                    </span>
                );
            },
        };
    }, [handleCellDoubleClick, localDateRange]);

    const columns = useMemo(
        () =>
            profitAndLoss.data
                ? toReportLookColumns({
                      periods: profitAndLoss.data.periods,
                      labelColumn,
                      periodColumns: buildPeriodColumns(
                          profitAndLoss.data.periods,
                          amountCellFn,
                          isBlocked ? styles.blurredValueCell : undefined,
                      ),
                  })
                : [labelColumn],
        [profitAndLoss.data, isBlocked, amountCellFn, labelColumn],
    );

    // ledgerBasis is always cash for PL — user cannot change it.
    // If this ever becomes configurable, update the apply handler too.
    const plExtraViewConfig = useMemo(
        () => ({ ledgerBasis: AccountingLedgerBasis.cash }),
        [],
    );

    const { viewConfig, refCtx } = useViewConfigSync({
        localDateRange,
        localCycle,
        comparisonOptions,
        setLocalDateRange,
        setLocalCycle,
        setComparisonOptions,
        rows,
        extraViewConfig: plExtraViewConfig,
    });

    const pendingRefCtx = usePendingReferenceContextMaybe();

    const { columnsWithHighlight, handleSelectionRangeChange } =
        useProfitLossSelection({
            taskId: selectedTask.id,
            rows,
            columns,
            expandedIds,
            refCtx,
            viewConfig,
            onPendingReferenceChange: pendingRefCtx?.setPendingReference,
        });
    const rangeAction = useCellRangeChatAction();
    const isGridShown =
        profitAndLoss.isSuccess && !!profitAndLoss.data && !!rows;

    return (
        <div className={styles.container}>
            {isBlocked && (
                <div className={styles.blockedMessage}>
                    {pluralizeWithCount(
                        "task",
                        selectedTask.numberOfActiveBlockedByTasks ?? 0,
                    )}{" "}
                    {(selectedTask?.numberOfActiveBlockedByTasks ?? 0) > 1
                        ? "are"
                        : "is"}{" "}
                    blocking your P&L
                </div>
            )}
            <div className={styles.toolbar}>
                <div className={styles.filterGroup}>
                    <DateRangeFilterNext
                        dateRange={toDateOnlyDateRange(localDateRange)}
                        onChange={(range) =>
                            setLocalDateRange(toLocalDateRange(range))
                        }
                        buttonClass={styles.dateRangeFilter}
                        pinnedPresets={pinnedPresets}
                        onTogglePin={onTogglePin}
                    />
                    <AccountingReportCycleSelect
                        value={localCycle}
                        onChange={setLocalCycle}
                        showEntityOption
                        showClassOption
                        className={styles.cycleSelect}
                    />
                </div>
                <AccountingReportComparisonSelect
                    comparisons={comparisonOptions.comparisons}
                    comparisonDiffs={comparisonOptions.comparisonDiffs}
                    ratioTypes={comparisonOptions.ratioTypes}
                    availableOptions={
                        ACCOUNTING_REPORT_COMPARISON_OPTIONS[
                            AccountingReportType.PROFIT_LOSS
                        ]
                    }
                    onChange={setComparisonOptions}
                />
                <div className="ml-auto">
                    <ExpandCollapseAllButton
                        isCollapsed={isCollapsed}
                        onExpandAll={handleExpandAll}
                        onCollapseAll={handleCollapseAll}
                    />
                </div>
            </div>
            <div className={styles.tableSection}>
                <LoaderOnInitialLoading queryResult={profitAndLoss}>
                    {profitAndLoss.data && rows && (
                        <SpreadsheetGrid
                            data={rows}
                            columns={columnsWithHighlight}
                            appearance="report"
                            centerColumns={hasSingleValueColumn(
                                profitAndLoss.data.periods,
                            )}
                            expandable
                            defaultExpandedIds={expandedIdsSeed}
                            clearSelectionOnBlur={false}
                            nestingIndentPx={24}
                            overflow="auto"
                            stickyHeader
                            readonly
                            disableDragFill
                            noOuterBorder
                            onExpandedChange={setExpandedIds}
                            onSelectionRangeChange={handleSelectionRangeChange}
                            rangeAction={rangeAction}
                            floatingFooter={
                                <CommandIsland sticky alignEnd>
                                    <ProfitLossTaskCommandBar />
                                </CommandIsland>
                            }
                        />
                    )}
                </LoaderOnInitialLoading>
            </div>
            {!profitAndLoss.isLoading && !isGridShown && (
                <CommandIsland sticky alignEnd className={styles.toolbar}>
                    <ProfitLossTaskCommandBar />
                </CommandIsland>
            )}
            {entityId != null && (
                <PLTaskGLDrilldownDrawer
                    open={drawerState != null}
                    onClose={handleDrawerClose}
                    accountCodes={drawerState?.accountCodes ?? []}
                    startDate={drawerState?.startDate ?? ""}
                    endDate={drawerState?.endDate ?? ""}
                    entityId={entityId}
                    ledgerId={ledgerId}
                    counterpartiesIds={
                        drawerState?.counterpartyFilter?.counterpartiesIds
                    }
                    withEmptyOrUnverifiedCounterparty={
                        drawerState?.counterpartyFilter
                            ?.hasEmptyOrUnverifiedCounterparty
                    }
                    ledgerBasis={filters.ledgerBasis}
                />
            )}
        </div>
    );
};
